/* Importing the necessary libraries and files */
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

/* Importing the components from the Material UI library. */
import { Autocomplete, TextField } from '@mui/material';

/* Importing the necessary functions from the Redux store. */
import { getDevice, getDeviceAll } from '../features/device/deviceSlice';

AutoCompleteDevice.propTypes = {
  setFieldValue: PropTypes.func,
  fieldName: PropTypes.string,
  values: PropTypes.object,
  touched: PropTypes.object,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  deviceID: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
};

/**
 * AutoCompleteDevice component.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.setFieldValue - The function to set the field value.
 * @param {string} props.fieldName - The name of the field.
 * @param {Object} props.values - The values object.
 * @param {Object} props.touched - The touched object.
 * @param {Object} props.errors - The errors object.
 * @param {boolean} [props.readOnly=false] - Whether the component is read-only.
 * @param {string} props.deviceID - The ID of the device.
 * @param {string} props.label - The label for the component.
 * @param {boolean} [props.disabled=false] - Whether the component is disabled.
 * @returns {JSX.Element} The AutoCompleteDevice component.
 */
export default function AutoCompleteDevice(props) {
  const {
    setFieldValue,
    fieldName,
    values,
    touched,
    errors,
    readOnly = false,
    deviceID,
    label,
    disabled = false,
  } = props;

  const dispatch = useDispatch();

  const { Devices, loading: deviceLoading } = useSelector((state) => state.device);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const currentValue = useMemo(
    () => deviceOptions.find((option) => option.id === values[fieldName]) || { name: 'None', id: '' },
    [deviceOptions, values, fieldName]
  );

  const setDeviceDefaultOptions = useCallback(() => {
    setDeviceOptions(
      Devices.map((p) => ({
        name: p.deviceName[0].name || p.id,
        id: p.id,
      }))
    );
  }, [Devices]);

  const initializeDeviceOptions = useCallback(async () => {
    setDeviceDefaultOptions();
  }, [setDeviceDefaultOptions]);

  useEffect(() => {
    initializeDeviceOptions();
  }, [initializeDeviceOptions]);

  useEffect(() => {
    if (deviceID) {
      dispatch(getDevice(deviceID));
    } else {
      dispatch(getDeviceAll());
    }
  }, [dispatch, deviceID]);

  return (
    <>
      {deviceLoading ? (
        <TextField
          fullWidth
          disabled={disabled}
          label="Device loading..."
          variant={readOnly ? 'standard' : 'outlined'}
          InputProps={{
            readOnly: true,
          }}
        />
      ) : (
        <Autocomplete
          fullWidth
          readOnly={readOnly}
          disabled={disabled}
          options={[...deviceOptions, { name: 'None', id: '' }]}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          value={currentValue}
          onChange={(e, value) => {
            setFieldValue(fieldName, value?.id);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label || 'Device'}
              error={Boolean((touched ? touched[fieldName] : false) && (errors ? errors[fieldName] : false))}
              helperText={(touched ? touched[fieldName] : false) && (errors ? errors[fieldName] : false)}
              style={readOnly ? { border: 'none' } : {}}
              variant={readOnly ? 'standard' : 'outlined'}
            />
          )}
        />
      )}
    </>
  );
}
